import {
  Dialog,
  DialogAlert,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCategoryModal } from "@/context/category-modal-provider";
import useCategories from "@/hooks/use-categories";
import useCategoryLimits from "@/hooks/use-category-limits";
import { CirclePlus, Loader2, SquarePen, Target, Trash } from "lucide-react";
import React from "react";
import CategoryForm from "./form";
import { CategoryFormValues } from "./form.schema";
import CategoryLimitForm from "./limit-form";
import { CategoryLimitFormValues } from "./limit-form.schema";

export default function CategoryModal() {
  const { state, hide } = useCategoryModal();
  const {
    categoryCreateMutation,
    categoryEditMutation,
    categoryDeleteMutation,
    masterCategories,
  } = useCategories();

  const isLimitMode = state.mode === "limit";

  // This modal is mounted at the app root, so the limits query only runs when
  // the limit view is actually open. The Categories page shares the same query
  // key, so opening from there resolves straight out of the cache.
  const {
    getLimitForCategory,
    isLoading: loadingLimits,
    limitSaveMutation,
    limitClearMutation,
  } = useCategoryLimits({ enabled: state.open && isLimitMode });

  const existingLimit = getLimitForCategory(
    state.category ? Number(state.category.id) : undefined
  );

  const onSubmit = async (values: CategoryFormValues) => {
    if (state.mode === "edit" && state.category) {
      await categoryEditMutation.mutateAsync({
        id: state.category.id,
        data: {
          name: values.name,
          color: values.color,
          parent:
            masterCategories?.find((mc) => mc.id === values.parent) ||
            undefined,
        },
      });
    } else if (state.mode === "create") {
      const newCategory = await categoryCreateMutation.mutateAsync({
        name: values.name,
        color: values.color,
        parent:
          masterCategories?.find((mc) => mc.id === values.parent) || undefined,
      });
      // Notify the caller (e.g., transaction form) of the newly created category
      if (state.onCreateSuccess && newCategory) {
        state.onCreateSuccess(newCategory);
      }
    } else if (state.mode === "delete" && state.category) {
      await categoryDeleteMutation.mutateAsync(state.category.id);
    }
  };

  const onSubmitLimit = (values: CategoryLimitFormValues) => {
    if (!state.category) return;
    limitSaveMutation.mutate({
      categoryId: Number(state.category.id),
      amount: Number(values.amount),
    });
    hide();
  };

  // "No limit" is the absence of a row on the backend, so clearing a limit is
  // a DELETE — there is no null amount to PATCH.
  const onClearLimit = () => {
    if (!existingLimit) return;
    limitClearMutation.mutate(existingLimit.id);
    hide();
  };

  const getTitle = () => {
    if (state.mode === "edit") {
      return (
        <DialogAlert
          title="Edit Category"
          icon={
            <div className="p-2 bg-black-400/10 rounded-full flex justify-center items-center">
              <SquarePen size={24} />
            </div>
          }
        />
      );
    } else if (state.mode === "create") {
      return (
        <DialogAlert
          title="Create Category"
          icon={
            <div className="p-2 bg-black-400/10 rounded-full flex justify-center items-center">
              <CirclePlus size={24} />
            </div>
          }
        />
      );
    } else if (state.mode === "delete") {
      return (
        <DialogAlert
          title="Delete Category"
          description="This action cannot be undone."
          icon={
            <div className="p-2 bg-semantic-red/10 rounded-full flex justify-center items-center">
              <Trash size={24} />
            </div>
          }
        />
      );
    } else if (state.mode === "limit") {
      return (
        <DialogAlert
          title={existingLimit ? "Edit Monthly Limit" : "Set Monthly Limit"}
          description="Limits reset at the start of each month."
          icon={
            <div className="p-2 bg-black-400/10 rounded-full flex justify-center items-center">
              <Target size={24} />
            </div>
          }
        />
      );
    }
  };

  const getSubmitLabel = () => {
    if (state.mode === "edit") {
      return "Save";
    } else if (state.mode === "create") {
      return "Create";
    } else if (state.mode === "delete") {
      return "Delete";
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={hide}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
        </DialogHeader>
        {isLimitMode ? (
          loadingLimits && !existingLimit ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <CategoryLimitForm
              // Remount once the existing limit resolves so the amount field
              // picks up the right default value.
              key={`${state.category?.id}-${existingLimit?.id ?? "none"}`}
              categoryName={state.category?.name ?? "this category"}
              existingLimit={existingLimit}
              onSubmit={onSubmitLimit}
              onClear={onClearLimit}
              onCancel={hide}
              isSubmitting={limitSaveMutation.isLoading}
              isClearing={limitClearMutation.isLoading}
            />
          )
        ) : (
          <CategoryForm
            onSubmit={(values) => {
              onSubmit(values);
              hide();
            }}
            defaultValues={
              state.category
                ? {
                    name: state.category.name,
                    color:
                      typeof state.category.color === "string"
                        ? state.category.color
                        : String(state.category.color),
                    parent:
                      typeof state.category.parent?.id === "number"
                        ? state.category.parent.id
                        : undefined,
                  }
                : {}
            }
            isSubmitting={
              categoryCreateMutation.isLoading ||
              categoryEditMutation.isLoading ||
              categoryDeleteMutation.isLoading
            }
            submitLabel={getSubmitLabel()}
            cancelLabel="Cancel"
            onCancel={hide}
            mode={state.mode === "limit" ? "create" : state.mode}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
